import { useState, useEffect } from "react";
import LoginApi from "../Api/LoginApi";
import ModuleApi from "../Api/ModuleApi";

let permissionsCache = null;
let userTypeCache = "";
let roleCodeCache = "";
let modulesCache = null;
let isFetching = false;
let subscribers = [];

const usePermissions = () => {
  const [permissions, setPermissions] = useState(permissionsCache || []);
  const [userType, setUserType] = useState(userTypeCache || "");
  const [roleCode, setRoleCode] = useState(roleCodeCache || "");
  const [modules, setModules] = useState(modulesCache || []);
  const [loading, setLoading] = useState(!permissionsCache || !modulesCache);

  useEffect(() => {
    if (permissionsCache && modulesCache) {
      setLoading(false);
      return;
    }

    const fetchAllData = async () => {
      if (isFetching) {
        subscribers.push({
          setPermissions,
          setUserType,
          setRoleCode,
          setModules,
          setLoading,
        });
        return;
      }

      isFetching = true;
      try {
        // Fetch Profile & Modules in parallel for better performance
        const [profileResult, modulesResult] = await Promise.all([
          LoginApi.getRolesAndPermissions(),
          ModuleApi.getModules(),
        ]);

        if (profileResult.status && modulesResult.status) {
          const profileData = profileResult.response.data;
          const modulesData = modulesResult.response.data;

          userTypeCache = profileData.userType || "";
          roleCodeCache = profileData.role?.code || "";
          permissionsCache = profileData.permissions || [];
          modulesCache = modulesData || [];

          setUserType(userTypeCache);
          setRoleCode(roleCodeCache);
          setPermissions(permissionsCache);
          setModules(modulesCache);

          subscribers.forEach((sub) => {
            sub.setUserType(userTypeCache);
            sub.setRoleCode(roleCodeCache);
            sub.setPermissions(permissionsCache);
            sub.setModules(modulesCache);
            sub.setLoading(false);
          });
          subscribers = [];
        }
      } catch (error) {
        console.error("Error fetching permissions or modulesData:", error);
      } finally {
        setLoading(false);
        isFetching = false;
      }
    };

    fetchAllData();
  }, []);

  /**
   * Normalizes strings for robust matching (lowercase, trimmed)
   */
  const normalize = (str) => (str ? str.toLowerCase().replace(/\s+/g, "").trim() : "");

  /**
   * Checks if the user has permission for a specific module and action.
   * @param {string} moduleName - The name of the module (e.g., "Blog", "Admin Users").
   * @param {string} action - The action type ("view", "add", "edit", "delete").
   * @returns {boolean}
   */
  const hasPermission = (moduleName, action = "view") => {
    // 1. While loading, default to false to prevent layout shifts/unauthorized flashes
    if (loading) return false;

    // 2. Superadmin bypass: Check for userType "ADMIN" or role code "SUPER_ADMIN"
    if (userType === "ADMIN" || roleCode === "SUPER_ADMIN" || roleCode === "superadmin") {
      return true;
    }

    const normalizedTarget = normalize(moduleName);

    const targetModule = modules.find(
      (m) => normalize(m.name) === normalizedTarget || normalize(m.code) === normalizedTarget
    );

    if (!targetModule) {
      const directMatch = permissions.find((p) => p.moduleId === moduleName);
      return directMatch?.actions?.[action] || false;
    }

    const userPerm = permissions.find(
      (p) => p.moduleId === targetModule.id || p.moduleId === targetModule._id
    );

    return !!userPerm?.actions?.[action];
  };

  return { hasPermission, userType, roleCode, loading, permissions, modules };
};

export default usePermissions;
